/**
 * AssetLoadingGate.tsx
 *
 * On-theme loading overlay shown once when the player clicks "Begin" in
 * CharSelect â€” i.e. the moment gamePhase transitions to "playing".
 *
 * Behaviour (gate-first sequencing, 2026-06-13):
 * - Mounts (visible) the instant gamePhase becomes "playing".
 * - Calls `preloadGameAssetsCritical(onProgress)` to fetch the critical subset
 *   (player sprites, terrain backgrounds, facility sprites, board image,
 *   intro + era-1 BGM â€” 56 URLs incl. the 6 opening-cutscene frames).
 * - Displays a VT323/parchment-palette progress bar ticking 0â€”100%.
 * - Dismisses automatically when progress reaches 100%. On dismiss, calls
 *   store.startIntroAfterGate() which triggers the intro cutscene + its BGM
 *   crossfade. The intro cutscene therefore NEVER starts until the gate is
 *   fully satisfied — eliminating the intro-BGM-vs-fetch race on cold iPad.
 * - STALL DETECTION: if no new asset has completed for STALL_MS (8s) while
 *   progress < 100%, a "Start anyway" button appears. The player can choose to
 *   proceed; on click the gate dismisses and startIntroAfterGate() fires.
 * - FAST-SKIP: if 100% is hit within SKIP_IF_DONE_MS (300ms) the overlay is
 *   never shown (assets were cached â€” returning player path). startIntroAfterGate()
 *   still fires so the cutscene always starts after gate logic completes.
 *
 * z-index: 2300 â€” above CutscenePlayer (2200). CutscenePlayer will not mount
 * until startIntroAfterGate() fires (cutsceneGroupId is null until then).
 * The z-order is kept as a belt-and-suspenders safety net.
 *
 * showCutscenes=false path: gate completes â†’ startIntroAfterGate() â†’
 * playCutscene() no-ops (showCutscenes guard inside) â†’ straight to board.
 *
 * VISIBLE WHEN: gamePhase === "playing" AND gate not yet dismissed.
 * NEVER blocks main menu or char-select.
 *
 * TEST PATH:
 *   MainMenu â†’ Start Game â†’ CharSelect â†’ Begin â†’ AssetLoadingGate renders â†’
 *   progress ticks to 100% â†’ overlay dismisses â†’ startIntroAfterGate() called
 *   â†’ intro cutscene starts â†’ cutscene ends â†’ board visible.
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { preloadGameAssetsCritical } from "../../assets/preloadAssets.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./AssetLoadingGate.module.css";

/**
 * If no asset completes for this many ms while progress < 100%,
 * reveal the "Start anyway" escape hatch.
 */
export const STALL_MS = 8000;

/**
 * If assets finish within this window, skip the overlay entirely.
 * Returning players with a warm cache should never see the gate.
 */
export const SKIP_IF_DONE_MS = 300;

/** Interval at which the stall-detection check runs. */
const STALL_CHECK_INTERVAL_MS = 500;

type GateState = "idle" | "loading" | "stalled" | "dismissing" | "done";

export function AssetLoadingGate(): React.ReactElement | null {
  const gamePhase = useGameStore((s) => s.gamePhase);

  // gateState drives visibility; progress is a 0â€”100 integer.
  const [gateState, setGateState] = useState<GateState>("idle");
  const [progress, setProgress] = useState(0);

  // Track the previous gamePhase to detect the char-select â†’ playing edge.
  const prevPhaseRef = useRef<string>(gamePhase);
  // Guard: ensure we only start the gate once per "playing" transition.
  const gateStartedRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Ref mirror of gateState so callbacks can read it stale-free.
  const gateStateRef = useRef<GateState>("idle");
  // Timestamp of last onProgress tick â€” used by stall detection.
  const lastProgressTsRef = useRef<number>(0);
  /**
   * Generation counter â€” incremented each time a new gate session starts (each
   * char-select â†’ playing transition). Every callback captures its own
   * generation number at birth; if the live counter has advanced by the time
   * the callback fires, the callback belongs to a past session and is a no-op.
   *
   * This replaces the old closure-local `let dismissed` approach (which broke
   * when the variable was needed in the JSX onClick) and is also more robust
   * than a shared dismissedRef (which would be reset to false by a new session
   * before stale callbacks from the old session have all fired).
   *
   * JSX onClick reads `gateGenRef.current` (the live session) and checks
   * against its own captured generation, giving it the same protection.
   */
  const gateGenRef = useRef(0);
  /**
   * dismissedRef: per-session dismissed flag, lives alongside gateGenRef.
   * Callbacks check BOTH: generation match (not a stale session) AND not yet
   * dismissed within this session. The onClick sets this to true so double-fire
   * via the .then() fallback becomes a no-op even within the same session.
   *
   * Reset to false at the start of each new gate session.
   */
  const dismissedRef = useRef(false);

  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = gamePhase;

    // Only trigger on the char-select â†’ playing transition.
    if (prev !== "char-select" || gamePhase !== "playing") return;
    if (gateStartedRef.current) return;
    gateStartedRef.current = true;

    // Mint a new generation for this gate session. All callbacks capture
    // `myGen` at creation time; if gateGenRef.current has advanced when they
    // fire, they know they belong to a past session and bail out immediately.
    const myGen = gateGenRef.current + 1;
    gateGenRef.current = myGen;
    dismissedRef.current = false;
    const startMs = Date.now();
    lastProgressTsRef.current = startMs;

    /** Returns true if this callback is stale (belongs to a superseded session). */
    const isStale = () => gateGenRef.current !== myGen;

    const dismiss = () => {
      if (isStale() || dismissedRef.current) return;
      dismissedRef.current = true;
      if (stallCheckRef.current !== null) {
        clearInterval(stallCheckRef.current);
        stallCheckRef.current = null;
      }
      if (fadeTimerRef.current !== null) clearTimeout(fadeTimerRef.current);
      setProgress(100);
      gateStateRef.current = "dismissing";
      setGateState("dismissing");
      // Remove overlay after CSS fade-out (350ms), then start the intro cutscene.
      // Gate-first sequencing: cutscene begins ONLY after gate fully completes so
      // the intro BGM Howler stream never races the asset preload fetches.
      fadeTimerRef.current = setTimeout(() => {
        gateStateRef.current = "done";
        setGateState("done");
        // Fire intro cutscene after overlay is gone (no-op if showCutscenes=false).
        useGameStore.getState().startIntroAfterGate();
      }, 380);
    };

    // Stall detection interval â€” runs every 500ms.
    // If the last progress tick was more than STALL_MS ago and we're not done,
    // transition to "stalled" state which reveals the "Start anyway" button.
    stallCheckRef.current = setInterval(() => {
      if (isStale() || dismissedRef.current) return;
      const s = gateStateRef.current;
      if (s === "stalled" || s === "dismissing" || s === "done") return;
      const sinceLastProgress = Date.now() - lastProgressTsRef.current;
      if (sinceLastProgress >= STALL_MS) {
        gateStateRef.current = "stalled";
        setGateState("stalled");
      }
    }, STALL_CHECK_INTERVAL_MS);

    // Start critical prefetch.
    void preloadGameAssetsCritical((loaded, total) => {
      if (isStale() || dismissedRef.current) return;
      // Record progress tick timestamp for stall detection.
      lastProgressTsRef.current = Date.now();

      const pct = total === 0 ? 100 : Math.round((loaded / total) * 100);
      setProgress(pct);

      if (pct >= 100) {
        const elapsed = Date.now() - startMs;
        if (elapsed < SKIP_IF_DONE_MS) {
          // Assets were cached — skip the overlay silently but still fire the
          // intro cutscene so the gate-first contract holds even on warm cache.
          dismissedRef.current = true;
          if (stallCheckRef.current !== null) {
            clearInterval(stallCheckRef.current);
            stallCheckRef.current = null;
          }
          if (fadeTimerRef.current !== null) clearTimeout(fadeTimerRef.current);
          gateStateRef.current = "done";
          setGateState("done");
          useGameStore.getState().startIntroAfterGate();
        } else {
          dismiss();
        }
      } else if (gateStateRef.current === "idle") {
        // First real progress tick after SKIP window â€” show the overlay.
        gateStateRef.current = "loading";
        setGateState("loading");
        // Un-stall if we somehow get a late progress tick after stalling.
      } else if (gateStateRef.current === "stalled") {
        // Progress resumed after stall â€” back to loading, reset stall timer.
        gateStateRef.current = "loading";
        setGateState("loading");
      }
    }).then(() => {
      // Fallback: promise resolved but onProgress may not have fired 100%.
      if (!isStale() && !dismissedRef.current) dismiss();
    });

    // Show the overlay after SKIP_IF_DONE_MS if still loading.
    const showTimer = setTimeout(() => {
      setGateState((s) => {
        if (s === "idle") {
          gateStateRef.current = "loading";
          return "loading";
        }
        return s;
      });
    }, SKIP_IF_DONE_MS);

    return () => {
      // Advance the generation counter â€” any in-flight callbacks from this
      // session will see gateGenRef.current !== myGen and bail immediately.
      // This covers the return-to-menu-mid-load path (F1): stale onProgress
      // and .then() callbacks from game N cannot fire against gate N+1.
      gateGenRef.current += 1;
      clearTimeout(showTimer);
      if (stallCheckRef.current !== null) {
        clearInterval(stallCheckRef.current);
        stallCheckRef.current = null;
      }
      if (fadeTimerRef.current !== null) clearTimeout(fadeTimerRef.current);
    };
  }, [gamePhase]);

  // Reset guard when game returns to menu / char-select so next game-start
  // can trigger a fresh gate.
  useEffect(() => {
    if (gamePhase === "menu" || gamePhase === "char-select") {
      gateStartedRef.current = false;
      gateStateRef.current = "idle";
      setGateState("idle");
      setProgress(0);
    }
  }, [gamePhase]);

  if (gateState === "idle" || gateState === "done") return null;

  const isDismissing = gateState === "dismissing";
  const isStalled = gateState === "stalled";
  const barDone = progress >= 100;

  return (
    <output
      className={`${styles.overlay}${isDismissing ? ` ${styles.dismissing}` : ""}`}
      data-testid="asset-loading-gate"
      aria-live="polite"
      aria-label="Loading game assets"
    >
      <div className={styles.card}>
        <h2 className={styles.title}>Preparing the Board</h2>
        <div className={styles.trackWrap}>
          <div className={styles.track}>
            <div
              className={`${styles.bar}${barDone ? "" : ` ${styles.barActive}`}`}
              style={{ width: `${progress}%` }}
              data-testid="asset-loading-gate-bar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              role="progressbar"
              tabIndex={-1}
            />
          </div>
          <span className={styles.pct} data-testid="asset-loading-gate-pct">
            {progress}%
          </span>
        </div>
        {isStalled ? (
          <>
            <p className={styles.hint} data-testid="asset-loading-gate-stall-hint">
              Connection is slow. Some assets may load in the background.
            </p>
            <button
              type="button"
              className={styles.stallBtn}
              data-testid="asset-loading-gate-start-anyway"
              onClick={() => {
                // Player explicitly chooses to proceed; neutralise any in-flight
                // fetch callbacks before starting the dismiss so they cannot
                // double-fire after the overlay fades.
                // Advance the generation so any still-in-flight onProgress/.then
                // callbacks see themselves as stale and become no-ops immediately.
                const s = gateStateRef.current;
                if (s === "stalled" || s === "loading") {
                  gateGenRef.current += 1;
                  dismissedRef.current = true;
                  if (stallCheckRef.current !== null) {
                    clearInterval(stallCheckRef.current);
                    stallCheckRef.current = null;
                  }
                  if (fadeTimerRef.current !== null) clearTimeout(fadeTimerRef.current);
                  setProgress((p) => p); // keep current pct visible during fade
                  gateStateRef.current = "dismissing";
                  setGateState("dismissing");
                  fadeTimerRef.current = setTimeout(() => {
                    gateStateRef.current = "done";
                    setGateState("done");
                    // Gate-first: fire intro cutscene after overlay gone.
                    useGameStore.getState().startIntroAfterGate();
                  }, 380);
                }
              }}
            >
              Start anyway
            </button>
          </>
        ) : (
          <p className={styles.hint}>Loading assets&hellip;</p>
        )}
      </div>
    </output>
  );
}
